import React, { useState } from "react";
import "./student.css";
import { useNavigate } from "react-router-dom";

function Student({ email = "" }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    studentName: "",
    mobile: "",
    email: email,
    accHolder: "",
    accNumber: "",
    ifsc: "",
    pan: "",
    aadhar: "",
  });

  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const rules = {
    studentName: /^[a-zA-Z\s]{1,100}$/,
    accHolder: /^[a-zA-Z\s]{1,100}$/,
    mobile: /^[0-9]{10}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    accNumber: /^[0-9]{9,18}$/,
    ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
    pan: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    aadhar: /^[0-9]{12}$/,
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (["studentName", "accHolder"].includes(name)) {
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setForm((p) => ({ ...p, [name]: value }));
      }
      return;
    }

    if (["mobile", "aadhar", "accNumber"].includes(name)) {
      if (/^[0-9]*$/.test(value)) {
        setForm((p) => ({ ...p, [name]: value }));
      }
      return;
    }

    if (name === "ifsc" || name === "pan") {
      value = value.toUpperCase();
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const validateForm = () => {
    const e = {};
    const f = {
      ...form,
      ifsc: form.ifsc.toUpperCase().trim(),
      pan: form.pan.toUpperCase().trim(),
      email: form.email.trim(),
      studentName: form.studentName.trim(),
      accHolder: form.accHolder.trim(),
    };

    if (!rules.studentName.test(f.studentName))
      e.studentName = "Only letters/spaces. Example: 'Abu Danish'.";

    if (!rules.mobile.test(f.mobile))
      e.mobile = "Mobile must be exactly 10 digits.";

    if (!rules.email.test(f.email)) e.email = "Enter a valid email.";

    if (!rules.accHolder.test(f.accHolder))
      e.accHolder = "Only letters/spaces as per bank.";

    if (!rules.accNumber.test(f.accNumber))
      e.accNumber = "Account number must be 9–18 digits.";

    if (!rules.ifsc.test(f.ifsc))
      e.ifsc = "IFSC looks like SBIN0XXXXXX (11 chars).";

    if (!rules.pan.test(f.pan))
      e.pan = "PAN must be 10 chars like ABCDE1234F (uppercase).";

    if (!rules.aadhar.test(f.aadhar)) e.aadhar = "Aadhaar must be 12 digits.";

    if (!agree) e.agree = "Please confirm that information is correct.";

    setErrors(e);
    return { ok: Object.keys(e).length === 0, fixed: f };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { ok, fixed } = validateForm();
    if (!ok) return;

    setSubmitting(true);
    setErrors({});
    try {
      const res = await fetch("https://nodei.ssccglpinnacle.com/api/student", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...fixed,
          agree,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.errors) {
          const serverErrs = Object.fromEntries(
            Object.entries(data.errors).map(([k, v]) => [k, String(v)])
          );
          setErrors(serverErrs);
          alert("Validation failed. Please check highlighted fields.");
        } else {
          alert(data.message || "Something went wrong.");
        }
        return;
      }

      alert("Saved!");

      setForm({
        studentName: "",
        mobile: "",
        email: email || "",
        accHolder: "",
        accNumber: "",
        ifsc: "",
        pan: "",
        aadhar: "",
      });
      setAgree(false);

      navigate(`/Studentdetail?email=${encodeURIComponent(email)}`);
    } catch (err) {
      console.error(err);
      alert("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const Err = ({ name }) =>
    errors[name] ? (
      <div
        className="student-error"
        style={{ color: "#d00", fontSize: 12, marginTop: 4 }}
      >
        {errors[name]}
      </div>
    ) : null;

  return (
    <div className="student-container">
      <form className="student-form" onSubmit={handleSubmit} noValidate>
        <h2 style={{ textAlign: "center" }}>Student Registration Form</h2>

        <div className="student-grid single-column">
          <div className="student-field">
            <label htmlFor="studentName" className="student-label">
              Student Name <span className="student-required">*</span>
            </label>
            <input
              id="studentName"
              name="studentName"
              className="student-input"
              type="text"
              value={form.studentName}
              onChange={handleChange}
              placeholder="Enter full name"
              autoComplete="name"
              required
            />
            <Err name="studentName" />
          </div>

          <div className="student-field">
            <label htmlFor="mobile" className="student-label">
              Mobile Number <span className="student-required">*</span>
            </label>
            <input
              id="mobile"
              name="mobile"
              className="student-input"
              type="text"
              value={form.mobile}
              onChange={handleChange}
              placeholder="10-digit mobile number"
              pattern="[0-9]{10}"
              maxLength={10}
              autoComplete="tel"
              required
            />
            <Err name="mobile" />
          </div>

          <div className="student-field">
            <label htmlFor="email" className="student-label">
              Email <span className="student-required">*</span>
            </label>
            <input
              id="email"
              name="email"
              className="student-input"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="name@example.com"
              autoComplete="email"
              required
              readOnly
              title="Email is taken from your login"
            />
            <Err name="email" />
          </div>

          <div className="student-field">
            <label htmlFor="accHolder" className="student-label">
              Bank Account Holder Name{" "}
              <span className="student-required">*</span>
            </label>
            <input
              id="accHolder"
              name="accHolder"
              className="student-input"
              type="text"
              value={form.accHolder}
              onChange={handleChange}
              placeholder="As per bank records"
              required
            />
            <Err name="accHolder" />
          </div>

          <div className="student-field">
            <label htmlFor="accNumber" className="student-label">
              Bank Account Number <span className="student-required">*</span>
            </label>
            <input
              id="accNumber"
              name="accNumber"
              className="student-input"
              type="text"
              value={form.accNumber}
              onChange={handleChange}
              placeholder="Enter account number"
              maxLength={18}
              autoComplete="off"
              required
            />
            <Err name="accNumber" />
          </div>

          <div className="student-field">
            <label htmlFor="ifsc" className="student-label">
              IFSC Code <span className="student-required">*</span>
            </label>
            <input
              id="ifsc"
              name="ifsc"
              className="student-input"
              type="text"
              value={form.ifsc}
              onChange={handleChange}
              placeholder="e.g., SBIN0XXXXXX"
              pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}"
              maxLength={11}
              autoComplete="off"
              required
            />
            <Err name="ifsc" />
          </div>

          <div className="student-field">
            <label htmlFor="pan" className="student-label">
              PAN Number <span className="student-required">*</span>
            </label>
            <input
              id="pan"
              name="pan"
              className="student-input"
              type="text"
              value={form.pan}
              onChange={handleChange}
              placeholder="ABCDE1234F"
              pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
              maxLength={10}
              autoComplete="off"
              required
            />
            <Err name="pan" />
          </div>

          <div className="student-field">
            <label htmlFor="aadhar" className="student-label">
              Aadhaar Card Number <span className="student-required">*</span>
            </label>
            <input
              id="aadhar"
              name="aadhar"
              className="student-input"
              type="text"
              value={form.aadhar}
              onChange={handleChange}
              placeholder="12-digit Aadhaar"
              pattern="[0-9]{12}"
              maxLength={12}
              autoComplete="off"
              required
            />
            <Err name="aadhar" />
          </div>
        </div>

        <div style={{ marginTop: "15px" }}>
          <label className="student-label" style={{ display: "flex", gap: 8 }}>
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
            />
            <span>I confirm that the information provided is correct.</span>
          </label>
          <Err name="agree" />
        </div>

        <div style={{ textAlign: "center", marginTop: "10px" }}>
          <button
            type="submit"
            disabled={!agree || submitting}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              backgroundColor: agree && !submitting ? "#4CAF50" : "#ccc",
              color: "#fff",
              fontSize: "16px",
              cursor: agree && !submitting ? "pointer" : "not-allowed",
              boxSizing: "border-box",
            }}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Student;
